/**
 * useAdvancedParamCount — data-driven count of Advanced-visibility parameters
 * for the currently open node.
 *
 * Both the drawer's Advanced peer section and the Configure footer hint read
 * from this hook. Deriving the count at the shell level (not piped through
 * ParameterList's mount lifecycle) eliminates two categories of bugs:
 *
 * 1. **Same-count node switches**: With a callback-based approach, switching
 *    between nodes that happen to have the same advanced-param count would
 *    leave stale visibility because the callback dep only fires when the
 *    reported number changes. Direct derivation reacts to the node identity.
 *
 * 2. **Unmounted-reporter staleness**: A callback wired only to the Configure
 *    section's ParameterList disappears when the user navigates to a peer
 *    section, so subsequent manifest changes silently break section visibility.
 *    Reading from the same data sources ParameterList uses (manifest + swagger
 *    definitions + input reflection) closes that gap.
 *
 * The rule mirrors ParameterList's own filter for advanced params:
 *   - Prefer manifest params (all non-Swagger nodes): `visibility === Advanced
 *     && !hideInUI`.
 *   - Fall back to store's `parameterDefinitions` (Swagger/ApiConnection):
 *     same filter.
 *   - Final fallback to connector-input reflection: reserved keys
 *     (`authentication`, `headers`, `queries`, `retryPolicy`).
 *
 * Data-driven: no node-type conditionals. Every action, trigger, connector,
 * and agent flows through the same rule.
 */

import { useMemo } from 'react';
import { useNodeEditorStore, type SelectedNode } from '@/stores/nodeEditorStore';
import { useNodeOperationInfo } from '@/hooks/useNodeOperationInfo';
import { useOperationManifestData } from '@/hooks/useOperationManifest';
import { ParameterVisibility } from '@/types/operationParameter';

/**
 * Input-shape keys the connector reflection path (`generateParametersFromInputs`
 * in ParameterList) treats as advanced. Kept in a Set for O(1) lookup and to
 * make the source of truth explicit — if ParameterList adds another advanced
 * key, add it here too.
 */
const INPUT_ADVANCED_KEYS: ReadonlySet<string> = new Set([
  'authentication',
  'headers',
  'queries',
  'retryPolicy',
]);

export function useAdvancedParamCount(
  node: SelectedNode | null | undefined,
): number {
  const parameterValues = useNodeEditorStore((s) => s.parameterValues);
  const parameterDefinitions = useNodeEditorStore(
    (s) => s.parameterDefinitions,
  );

  // Same operationInfo → manifest lookup ParameterList performs. React Query
  // dedupes the fetch, so hoisting it here adds no network cost.
  const { operationInfo } = useNodeOperationInfo({
    nodeData: node?.nodeData,
    parameterValues,
  });
  const { parameters: manifestParams } = useOperationManifestData(operationInfo);

  const nodeInputs = (
    node?.nodeData as { inputs?: Record<string, unknown> } | undefined
  )?.inputs;

  return useMemo(() => {
    // Manifest is the primary source for most nodes (built-ins, agents,
    // service providers). Count matches ParameterList's Advanced filter,
    // minus the conditional-visibility gate — the peer-section gate cares
    // whether the node *can* surface advanced params, not whether every
    // dependency is currently satisfied.
    if (manifestParams && manifestParams.length > 0) {
      let count = 0;
      for (const p of manifestParams) {
        if (p.visibility === ParameterVisibility.Advanced && !p.hideInUI) {
          count++;
        }
      }
      if (count > 0) return count;
    }
    // Swagger-based ApiConnection nodes seed `parameterDefinitions` in the
    // store via `setSwaggerMetadata`. Same filter applies.
    if (parameterDefinitions && parameterDefinitions.length > 0) {
      let count = 0;
      for (const p of parameterDefinitions) {
        if (p.visibility === ParameterVisibility.Advanced && !p.hideInUI) {
          count++;
        }
      }
      if (count > 0) return count;
    }
    // Reflection fallback — connector inputs with no manifest available yet
    // (e.g. some ApiConnection triggers) still surface Advanced via the
    // reserved-keys shortlist ParameterList uses in `generateParametersFromInputs`.
    if (nodeInputs && typeof nodeInputs === 'object') {
      let count = 0;
      for (const key of Object.keys(nodeInputs)) {
        if (INPUT_ADVANCED_KEYS.has(key)) count++;
      }
      return count;
    }
    return 0;
  }, [manifestParams, parameterDefinitions, nodeInputs]);
}
